import { useRef, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { useMe } from '../api/hooks'
import { BusyLine } from './ui/BusyLine'
import { useBusyLine } from './ui/useBusyLine'

/**
 * S2-D11's routing invariant: a user with no profile is ALWAYS routed to
 * first run, never rendered empty.
 *
 * This was the first route gate the app ever had. When it landed there were no
 * others -- `router.tsx` has no loader, `Layout` checked nothing, and auth was
 * enforced purely reactively by `apiFetch`'s 401 handler (`api/client.ts:38`).
 * That works for auth and does nothing here, because a profile-less account
 * does not 401: `GET /api/profile` answers 404 and the Dashboard renders empty
 * containers. Spec 1's Task 13 met exactly that on production, with no way to
 * repair it from inside the app.
 *
 * `Layout` NOW CARRIES A SECOND, EARLIER GATE, and the two do different jobs.
 * That one is synchronous (`hasSession()`, no request) and answers "is there a
 * session at all", so a signed out visitor never sees the chrome. This one is
 * asynchronous and answers "does that session have a profile". Neither
 * subsumes the other: a token with no profile passes the first and is caught
 * here.
 *
 * It wraps the shell's `<Outlet/>` rather than each page, so every routed page
 * is behind it and a later one cannot forget to opt in -- which is the
 * property the spec chose over a create-both-rows transaction. Note it wraps
 * the OUTLET specifically, not the whole shell: `Sidebar` and `CoachDock` are
 * siblings of this guard in `Layout`, so they still render for a user in first
 * run. That is harmless only because /first-run is routed outside the shell
 * entirely (router.tsx) and so never mounts Layout at all.
 */
export function RequireFirstRun({ children }: { children: ReactNode }) {
  const { data, isLoading, isError, dataUpdatedAt, errorUpdatedAt } = useMe()
  const location = useLocation()

  // Render NOTHING while unknown. Rendering the app optimistically shows the
  // empty containers this exists to prevent; flashing first run at someone who
  // HAS a profile is worse still, because it reads as their account being gone.
  //
  // Unknown means *not yet answered*, not "loading". An /api/me that failed is
  // asked again whenever a page reading it mounts (`useMe` has `staleTime: 0`),
  // and a query with no data goes back to loading while it is re-asked. Gating
  // on `isLoading` alone therefore unmounted Settings and Profile, whose mount
  // re-asked, whose failure mounted them again: a blank page and a request a
  // second, forever (found verifying Spec 2 D5 in a browser, #216).
  const answered = dataUpdatedAt > 0 || errorUpdatedAt > 0
  if (isLoading && !answered) return <WhileUnknown />

  // On error, fall through to the app. A failed /api/me is a transient network
  // problem or a 401 that `apiFetch` has already redirected on -- and blocking
  // the whole app behind one flaky request would be a worse failure than the
  // one this guards against.
  if (isError || !data) return <>{children}</>

  if (!data.first_run_complete && location.pathname !== '/first-run') {
    return <Navigate to="/first-run" replace />
  }
  return <>{children}</>
}

/**
 * What this guard draws while it does not yet know: nothing, except the busy line.
 *
 * This is the first request every page load makes, and behind a busy server it
 * is asked again for about 13 s (#392) before the guard lets the app through. A
 * guard that drew nothing for all of it would leave the reader on a blank page
 * with no word, and the busy line would appear only after the page's panels had
 * started their own requests: the surface that asked first would be the one that
 * said nothing. So it is the one place this guard draws anything while unknown,
 * and only when the server has been refusing requests (`useBusyLine`). Normally,
 * with `/api/me` answered in a few milliseconds, it is an empty region, and the
 * "render nothing while unknown" rule above holds exactly as written.
 */
function WhileUnknown() {
  const ref = useRef<HTMLDivElement>(null)
  const busy = useBusyLine(ref)
  return (
    // The Dashboard's own frame (`mx-auto max-w-[1600px] px-4 sm:px-6`), and no top
    // padding: the line is first on the page, where the Dashboard's own skeleton
    // puts it, so it does not jump when the gate lets the page through.
    <div ref={ref} role="status" className={busy ? 'mx-auto max-w-[1600px] px-4 sm:px-6' : undefined}>
      {busy && <BusyLine />}
    </div>
  )
}
